import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { ArrowRight, Newspaper } from "lucide-react";
import { SectionLabel, HighlightText } from "@/components/Bits";
import { blogSupabase, isBlogConfigured } from "@/lib/blogSupabase";

function formatDate(value) {
  if (!value) return "";
  try {
    return format(new Date(value), "MMMM d, yyyy");
  } catch {
    return "";
  }
}

function PostCard({ post }) {
  return (
    <Link
      to={`/blogs/${post.slug}`}
      className="aidify-card overflow-hidden flex flex-col group"
      data-reveal
    >
      <div className="aspect-[16/9] bg-cream-200 overflow-hidden">
        {post.cover_image ? (
          <img
            src={post.cover_image}
            alt={post.title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-sage-500">
            <Newspaper size={36} strokeWidth={1.5} />
          </div>
        )}
      </div>
      <div className="p-6 flex flex-col flex-1">
        <p className="text-xs uppercase tracking-[0.12em] text-forest-400/70">
          {formatDate(post.published_at)}
        </p>
        <h2 className="mt-3 font-display font-bold text-forest-600 text-xl leading-snug">
          {post.title}
        </h2>
        {post.excerpt && (
          <p className="mt-3 text-forest-400 text-[15px] leading-relaxed line-clamp-3">
            {post.excerpt}
          </p>
        )}
        <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-forest-600 group-hover:gap-2.5 transition-all">
          Read article <ArrowRight size={15} />
        </span>
      </div>
    </Link>
  );
}

function StateBlock({ title, children }) {
  return (
    <div className="mt-16 mb-10 max-w-xl" data-reveal>
      <h2 className="font-display font-bold text-forest-600 text-2xl">{title}</h2>
      <p className="mt-3 text-forest-400 text-[15px] leading-relaxed">{children}</p>
    </div>
  );
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;

    async function load() {
      if (!isBlogConfigured) {
        setLoading(false);
        return;
      }
      try {
        const { data, error: fetchError } = await blogSupabase
          .from("posts")
          .select("id, title, slug, excerpt, cover_image, published_at")
          .eq("status", "published")
          .in("site", ["aidify", "both"])
          .order("published_at", { ascending: false });

        if (!active) return;
        if (fetchError) throw fetchError;
        setPosts(data || []);
      } catch (e) {
        // TEMPORARY (blog schema-migration cutover): the UI renders a generic
        // "couldn't load the blog" state at HTTP 200, which is indistinguishable
        // from an empty blog. Log the real Supabase error so a failed cutover
        // (unexposed schema, missing GRANT, bad key) is visible in the browser
        // console. Remove once the migration is verified.
        console.error("[blogSupabase] Blog list query failed:", e);
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, []);

  // The global reveal observer in Layout runs once on mount, before the posts
  // have been fetched, so the asynchronously-rendered cards never receive the
  // `is-visible` class and stay at opacity:0. Re-run the observer once content
  // is in the DOM so the cards fade in like the rest of the page.
  useEffect(() => {
    if (loading) return;
    const els = document.querySelectorAll("[data-reveal]:not(.is-visible)");
    if (!els.length) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => e.isIntersecting && e.target.classList.add("is-visible")),
      { rootMargin: "0px 0px -10% 0px", threshold: 0.05 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [loading, posts]);

  return (
    <>
      <Helmet>
        <title>Blog | Aidify — Insights on Virtual Assistance & Operations</title>
        <meta
          name="description"
          content="Practical insights on bookkeeping, virtual assistance, customer support, and scaling operations with Aidify."
        />
      </Helmet>

      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 pt-10 lg:pt-16 pb-16 lg:pb-24">
        {/* HERO */}
        <div className="max-w-3xl" data-reveal>
          <SectionLabel>The Aidify Blog</SectionLabel>
          <h1 className="mt-6 font-display font-extrabold tracking-tight text-forest-600 text-4xl md:text-6xl leading-[1.0]">
            Ideas to run{" "}
            <span className="font-serif-italic font-normal">leaner</span> &amp;{" "}
            <HighlightText>
              <span className="font-serif-italic font-normal">smarter</span>
            </HighlightText>
          </h1>
          <p className="mt-5 text-forest-400 text-[15px] md:text-base leading-relaxed">
            Practical perspectives on bookkeeping, virtual assistance, customer support, and the
            operational habits that help businesses grow without the overhead.
          </p>
        </div>

        {/* STATES */}
        {loading && (
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {[0, 1, 2].map((i) => (
              <div key={i} className="aidify-card overflow-hidden">
                <div className="aspect-[16/9] bg-cream-200 animate-pulse" />
                <div className="p-6 space-y-3">
                  <div className="h-3 w-24 bg-cream-200 rounded animate-pulse" />
                  <div className="h-5 w-3/4 bg-cream-200 rounded animate-pulse" />
                  <div className="h-4 w-full bg-cream-200 rounded animate-pulse" />
                  <div className="h-4 w-2/3 bg-cream-200 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && error && (
          <StateBlock title="We couldn't load the blog">
            Something went wrong while fetching our latest articles. Please refresh the page or check
            back in a little while.
          </StateBlock>
        )}

        {!loading && !error && posts.length === 0 && (
          <StateBlock title="No articles yet">
            We're working on our first posts. Check back soon for insights from the Aidify team.
          </StateBlock>
        )}

        {!loading && !error && posts.length > 0 && (
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {posts.map((post) => (
              <PostCard key={post.id} post={post} />
            ))}
          </div>
        )}
      </section>
    </>
  );
}
